import { IncomingMessage, ServerResponse } from 'http'
import { jwtDecode } from 'jwt-decode'
import Cookies from 'universal-cookie'
import { v4 } from 'uuid'
import { ANGEL_AUTH_TOKEN } from '@/components/context/AuthContext/utils'
import ServerSegmentHandler from '@/services/analytics/SegmentHandler/ServerSegmentHandler'
import { Durations } from '../../constants/durations'
import { AppFeatures } from './app-features'
import { ServerExperiments } from './types'
import { getGrowthbook, trackExperimentExposure } from './utils'

const SEGMENT_ANONYMOUS_ID_COOKIE = 'ajs_user_id'

function getAnonymousId(cookies: Partial<{ [key: string]: string }>): {
  anonymousId: string
  shouldSetCookie: boolean
} {
  const anonId = new Cookies(cookies).get(SEGMENT_ANONYMOUS_ID_COOKIE)

  if (anonId) {
    return { anonymousId: anonId, shouldSetCookie: false }
  } else {
    return { anonymousId: v4(), shouldSetCookie: true }
  }
}

function getUserId(cookies: Partial<{ [key: string]: string }>): string | null {
  const authToken = new Cookies(cookies).get(ANGEL_AUTH_TOKEN)

  if (!authToken) return null
  const decoded = jwtDecode(authToken)
  return decoded.sub || null
}

export const initializeGrowthbookOnServer = async (
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse
) => {
  const { anonymousId, shouldSetCookie } = getAnonymousId(req.cookies)
  if (shouldSetCookie) {
    res.setHeader(
      'Set-Cookie',
      `${encodeURIComponent(SEGMENT_ANONYMOUS_ID_COOKIE)}=${encodeURIComponent(
        anonymousId
      )}; Max-Age=${Durations.TEN_YEARS_IN_SECONDS}; Path=/`
    )
  }

  const userId = getUserId(req.cookies)

  const growthbook = getGrowthbook({
    trackingCallback: (experiment, result) => {
      trackExperimentExposure(experiment, result, (name, properties) =>
        ServerSegmentHandler.track(name, {
          ...properties,
          anonymousId,
          userId: userId ?? undefined,
        })
      )
    },
  })
  // Give GB two attempts to initialize
  const gbRes = await growthbook.init()
  if (gbRes.error) await growthbook.init()

  await growthbook.setAttributes({
    id: anonymousId,
    userId: userId ?? undefined,
  })
  return growthbook
}
export const setupServerExperiments = async (
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse
) => {
  const growthbook = await initializeGrowthbookOnServer(req, res)
  return {
    getFeatureValue: growthbook.getFeatureValue.bind(growthbook),
    destroy: growthbook.destroy.bind(growthbook),
    anonymousId: growthbook.getAttributes().id,
  }
}

const getExperimentValueFromQuery = (fromQuery: string) => {
  if (fromQuery.toLowerCase() === 'true') return true
  if (fromQuery.toLowerCase() === 'false') return false
  if (Number.isFinite(Number(fromQuery))) return Number(fromQuery)
  return fromQuery
}

const getSearchParam = (req: IncomingMessage, key: string): string | null => {
  if (!req.url) return null
  const protocol =
    req.headers['x-forwarded-proto'] === 'https' ? 'https' : 'http'
  const url = new URL(req.url, `${protocol}://${req.headers.host}`)
  return url.searchParams.get(key)
}

const getOverrideValue = (req: IncomingMessage, experimentKey: string) => {
  const overrideEnv = process.env.EXPERIMENT_OVERRIDE_VALUES || ''
  const overrideMap = overrideEnv.split(',').reduce((acc, pair) => {
    const [key, value] = pair.split('=')
    if (key && value !== undefined) {
      acc[key.trim()] = value.trim()
    }
    return acc
  }, {} as Record<string, string>)

  if (overrideMap[experimentKey]) {
    return getExperimentValueFromQuery(overrideMap[experimentKey])
  }

  const fromQuery = getSearchParam(req, experimentKey)
  if (fromQuery !== null) {
    return getExperimentValueFromQuery(fromQuery)
  }

  return null
}

interface ExperimentParams<T extends keyof AppFeatures> {
  experimentKey: T
  defaultValue: AppFeatures[T]
}

type ExperimentParamsUnion = {
  [K in keyof AppFeatures]: ExperimentParams<K>
}[keyof AppFeatures]

export type ExperimentParamsArray = ExperimentParamsUnion[]

export async function runExperimentsOnServer(
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> },
  res: ServerResponse,
  experiments: ExperimentParamsArray
): Promise<ServerExperiments> {
  const { getFeatureValue, destroy } = await setupServerExperiments(req, res)

  const result = experiments.reduce((prev, { experimentKey, defaultValue }) => {
    const overrideValue = getOverrideValue(req, experimentKey)
    const v = overrideValue ?? getFeatureValue(experimentKey, defaultValue)

    // @ts-expect-error - Type returned from getExperimentValueFromQuery may be wider than current experiment types,
    // but this helps future proof if we add experiments with different value types in the future
    prev[experimentKey] = v
    return prev
  }, {} as ServerExperiments)

  destroy()
  return result
}
